import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { projectsAPI, chatsAPI } from '../services/api';
import { useChatStore } from '../hooks/useChatStore';
import { notify } from '../hooks/useNotificationStore';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import NewChatModal from '../components/NewChatModal';

interface Project {
  id: number;
  name: string;
  description: string;
  chat_count: number;
  created_at: string;
}

interface Chat {
  id: number;
  title: string;
  query_type_id: number;
  created_at: string;
}

export default function Project() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { queryTypes, fetchQueryTypes } = useChatStore();

  const [project, setProject] = useState<Project | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [newChatModalOpen, setNewChatModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{ id: number; title: string } | null>(null);

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadChats();
      fetchQueryTypes();
    }
  }, [projectId, fetchQueryTypes]);

  const loadProject = async () => {
    try {
      const response = await projectsAPI.get(parseInt(projectId!));
      setProject(response.data);
      setEditName(response.data.name);
      setEditDescription(response.data.description || '');
    } catch (error) {
      console.error('Failed to load project:', error);
      notify.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const loadChats = async () => {
    try {
      const response = await chatsAPI.list(parseInt(projectId!));
      setChats(response.data);
    } catch (error) {
      console.error('Failed to load chats:', error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await projectsAPI.update(parseInt(projectId!), {
        name: editName,
        description: editDescription,
      });
      setProject((prev) => prev ? { ...prev, name: editName, description: editDescription } : null);
      setEditing(false);
      notify.success('Project updated');
    } catch (error) {
      console.error('Failed to update project:', error);
      notify.error('Failed to update project');
    }
  };

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;

    try {
      await chatsAPI.delete(chatToDelete.id);
      setChats((prev) => prev.filter((c) => c.id !== chatToDelete.id));
      notify.success('Chat deleted', `"${chatToDelete.title}" has been deleted.`);
    } catch (error) {
      notify.error('Failed to delete chat');
    } finally {
      setChatToDelete(null);
    }
  };

  const handleCreateChat = async (queryTypeId: number) => {
    try {
      const response = await chatsAPI.create({
        query_type_id: queryTypeId,
        project_id: parseInt(projectId!),
      });
      setNewChatModalOpen(false);
      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create chat:', error);
      notify.error('Failed to create chat');
    }
  };

  const getQueryTypeName = (queryTypeId: number) => {
    const qt = queryTypes.find((q) => q.id === queryTypeId);
    return qt?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-surface-400 hover:text-surface-200 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Projects
        </Link>

        {/* Project header */}
        <div className="card mb-6">
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="input-field"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleSaveEdit} className="btn-primary">
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setEditName(project.name);
                    setEditDescription(project.description || '');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-surface-100">{project.name}</h1>
                {project.description && (
                  <p className="text-surface-400 mt-2">{project.description}</p>
                )}
                <p className="text-surface-500 text-sm mt-3">
                  {chats.length} {chats.length === 1 ? 'chat' : 'chats'}
                </p>
              </div>
              <button
                onClick={() => setEditing(true)}
                className="p-2 text-surface-400 hover:text-surface-200 transition-colors"
                title="Edit project"
              >
                <PencilIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Chats section */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-surface-100">Chats</h2>
          <button
            onClick={() => setNewChatModalOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            New Chat
          </button>
        </div>

        {chats.length === 0 ? (
          <div className="card text-center py-12">
            <ChatBubbleLeftRightIcon className="w-12 h-12 text-surface-600 mx-auto mb-4" />
            <p className="text-surface-400">No chats in this project yet</p>
            <button
              onClick={() => setNewChatModalOpen(true)}
              className="btn-primary mt-4"
            >
              Start a Chat
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {chats.map((chat) => (
              <div
                key={chat.id}
                className="group card flex items-center justify-between hover:bg-surface-700/50 transition-colors"
              >
                <Link
                  to={`/chat/${chat.id}`}
                  className="flex items-center gap-3 flex-1 min-w-0"
                >
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-surface-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-surface-100 truncate">{chat.title}</p>
                    <p className="text-surface-500 text-xs">
                      {getQueryTypeName(chat.query_type_id)}
                    </p>
                  </div>
                </Link>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setChatToDelete({ id: chat.id, title: chat.title });
                  }}
                  className="p-2 rounded opacity-0 group-hover:opacity-100 text-surface-400 hover:text-red-400 hover:bg-surface-600 transition-all"
                  title="Delete chat"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Chat Modal - custom version for project */}
      {newChatModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md bg-surface-800 border border-surface-700 rounded-2xl p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              New Chat in {project.name}
            </h3>
            <p className="text-sm text-surface-400 mb-4">
              Select a query type for your new chat
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {queryTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleCreateChat(type.id)}
                  className="w-full text-left p-4 rounded-xl bg-surface-700 hover:bg-surface-600 transition-colors"
                >
                  <h4 className="font-medium text-surface-100">{type.name}</h4>
                  <p className="text-sm text-surface-400 mt-1">{type.description}</p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setNewChatModalOpen(false)}
              className="w-full mt-4 btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Chat Confirmation */}
      <ConfirmDeleteModal
        isOpen={chatToDelete !== null}
        onClose={() => setChatToDelete(null)}
        onConfirm={handleDeleteChat}
        title="Delete Chat"
        message={`Are you sure you want to delete "${chatToDelete?.title}"? This will also permanently delete any documents associated with this chat.`}
      />
    </div>
  );
}
